import { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useCaps } from '../PlotEditorContext';
import { DataEncodingsTab } from './DataEncodingsTab';
import { AxesScalesTab } from './AxesScalesTab';
import { ColorsStylesTab } from './ColorsStylesTab';
import { LabelsAnnotationsTab } from './LabelsAnnotationsTab';
import { LegendGuidesTab } from './LegendGuidesTab';
import { LayoutSubplotsTab } from './LayoutSubplotsTab';
import { usePlotsStore } from '@/store/plots';
import type { PlotEditorTabId } from '@/store/plots';

interface PlotEditorTabsProps {
  activeTab: PlotEditorTabId;
  onTabChange: (tab: PlotEditorTabId) => void;
}

// Type guard to ensure only valid tab IDs are used
function isValidTabId(value: string): value is PlotEditorTabId {
  const validIds: PlotEditorTabId[] = [
    'data-encodings',
    'axes-scales',
    'colors-styles',
    'labels-annotations',
    'legend-guides',
    'layout-subplots',
  ];
  return validIds.includes(value as PlotEditorTabId);
}

/**
 * Wrapper component that conditionally renders tabs based on capabilities
 */
export function PlotEditorTabs({ activeTab, onTabChange }: PlotEditorTabsProps) {
  const caps = useCaps();
  const { selection } = usePlotsStore();
  
  // Determine which tabs should be visible
  const showAxesTab = caps.supports.axesTab;
  const showLegendTab = caps.supports.legend;
  
  // Show placeholder message when no selection is active
  const hasSelection = selection.kind !== 'none';
  
  // Ensure activeTab is valid, fallback to first available tab
  useEffect(() => {
    const availableTabs: PlotEditorTabId[] = [
      'data-encodings',
      showAxesTab && 'axes-scales',
      'colors-styles',
      'labels-annotations',
      showLegendTab && 'legend-guides',
      'layout-subplots',
    ].filter(Boolean) as PlotEditorTabId[];
    
    if (!availableTabs.includes(activeTab)) {
      console.warn(`Invalid activeTab "${activeTab}", falling back to "${availableTabs[0] || 'data-encodings'}"`);
      onTabChange(availableTabs[0] || 'data-encodings');
    }
  }, [activeTab, showAxesTab, showLegendTab, onTabChange]);

  // Handle Tabs component's string-based onValueChange with type safety
  const handleValueChange = (value: string) => {
    if (isValidTabId(value)) {
      onTabChange(value);
    } else {
      console.error(`Invalid tab ID received from Tabs component: "${value}"`);
    }
  };

  return (
    <Tabs 
      value={activeTab} 
      onValueChange={handleValueChange}
      className="flex-1 flex flex-col h-full"
    >
      {/* Horizontal tabs on top */}
      <div className="border-b border-border bg-muted/30">
        <div className="overflow-x-auto">
          <TabsList className="flex h-9 w-full gap-1 bg-transparent px-3 pt-2 pb-1">
            <TabsTrigger 
              value="data-encodings" 
              className="h-8 flex-1 rounded-full px-3 text-xs font-medium data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:text-foreground data-[state=active]:font-semibold data-[state=inactive]:text-muted-foreground hover:bg-muted"
            >
              Data
            </TabsTrigger>
            {showAxesTab && (
              <TabsTrigger 
                value="axes-scales" 
                className="h-8 flex-1 rounded-full px-3 text-xs font-medium data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:text-foreground data-[state=active]:font-semibold data-[state=inactive]:text-muted-foreground hover:bg-muted"
              >
                Axes
              </TabsTrigger>
            )}
            <TabsTrigger 
              value="colors-styles" 
              className="h-8 flex-1 rounded-full px-3 text-xs font-medium data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:text-foreground data-[state=active]:font-semibold data-[state=inactive]:text-muted-foreground hover:bg-muted"
            >
              Style
            </TabsTrigger>
            <TabsTrigger 
              value="labels-annotations" 
              className="h-8 flex-1 rounded-full px-3 text-xs font-medium data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:text-foreground data-[state=active]:font-semibold data-[state=inactive]:text-muted-foreground hover:bg-muted"
            >
              Labels
            </TabsTrigger>
            {showLegendTab && (
              <TabsTrigger 
                value="legend-guides" 
                className="h-8 flex-1 rounded-full px-3 text-xs font-medium data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:text-foreground data-[state=active]:font-semibold data-[state=inactive]:text-muted-foreground hover:bg-muted"
              >
                Legend
              </TabsTrigger>
            )}
            <TabsTrigger 
              value="layout-subplots" 
              className="h-8 flex-1 rounded-full px-3 text-xs font-medium data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:text-foreground data-[state=active]:font-semibold data-[state=inactive]:text-muted-foreground hover:bg-muted"
            >
              Layout
            </TabsTrigger>
          </TabsList>
        </div>
      </div>

      {/* Content area below tabs */}
      <div className="flex-1 overflow-hidden">
        {!hasSelection ? (
          <div className="flex-1 flex items-center justify-center p-6">
            <div className="text-center space-y-2 max-w-sm">
              <p className="text-sm text-muted-foreground">
                Click on the plot to start editing
              </p>
              <p className="text-xs text-muted-foreground/70">
                Click on bars, points, axes, legend items, or the plot title to see relevant controls
              </p>
            </div>
          </div>
        ) : (
          <ScrollArea className="h-full w-full">
            <div className="p-3 space-y-4">
              <TabsContent value="data-encodings" className="mt-0">
                <DataEncodingsTab />
              </TabsContent>
              {showAxesTab && (
                <TabsContent value="axes-scales" className="mt-0">
                  <AxesScalesTab />
                </TabsContent>
              )}
              <TabsContent value="colors-styles" className="mt-0">
                <ColorsStylesTab />
              </TabsContent>
              <TabsContent value="labels-annotations" className="mt-0">
                <LabelsAnnotationsTab />
              </TabsContent>
              {showLegendTab && (
                <TabsContent value="legend-guides" className="mt-0">
                  <LegendGuidesTab />
                </TabsContent>
              )}
              <TabsContent value="layout-subplots" className="mt-0">
                <LayoutSubplotsTab />
              </TabsContent>
            </div>
          </ScrollArea>
        )}
      </div>
    </Tabs>
  );
}









